import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { storeService } from "../services/store.service";
import { isAdmin } from '../utils/permissions';

export const fetchStores = createAsyncThunk("store/fetchAll", async (_, thunkAPI) => {
  try {
    return await storeService.getStores();
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch stores");
  }
});

export const fetchStoreById = createAsyncThunk("store/fetchById", async (id, thunkAPI) => {
  try {
    return await storeService.getStoreById(id);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch store");
  }
});

export const addStore = createAsyncThunk("store/add", async (store, thunkAPI) => {
  try {
    return await storeService.createStore(store);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to create store");
  }
});

export const editStore = createAsyncThunk("store/edit", async ({ id, store }, thunkAPI) => {
  try {
    return await storeService.updateStore(id, store);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to update store");
  }
});

export const removeStore = createAsyncThunk("store/remove", async (id, thunkAPI) => {
  try {
    await storeService.deleteStore(id);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to delete store");
  }
});

export const importStores = createAsyncThunk("store/import", async (file, thunkAPI) => {
  try {
    return await storeService.importStores(file);
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data || "Failed to import stores");
  }
});

const storeSlice = createSlice({
  name: "store",
  initialState: { stores: [], store: null, loading: false, error: null, showModal: false, showDeleteModal: false },
  reducers: {
    openModal: (state) => {
      state.showModal = true
    },
    closeModal: (state) => {
      state.showModal = false
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false
    },
    addPhotoToStore: (state, {payload}) => {
      state.stores = state.stores.map(store => {
        if (store.shopId === payload.shopId) {
          // If the new photo is principal, mark existing photos as non-principal
          const updatedExistingPhotos = payload.photo.isPrincipal
            ? (store.photos || []).map(photo => ({ ...photo, isPrincipal: false }))
            : (store.photos || []);

          return {...store, photos: [payload.photo, ...updatedExistingPhotos]};
        }
        return store;
      })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStores.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.loading = false;
        state.stores = action.payload;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStoreById.fulfilled, (state, action) => {
        state.store = action.payload;
      })
      .addCase(addStore.pending, (state) => {
        state.loading = true
      })
      .addCase(addStore.fulfilled, (state, action) => {
        state.stores.push(action.payload);
        state.loading = false
        state.showModal = false
      })
      .addCase(editStore.pending, (state) => {
        state.loading = true
      })
      .addCase(editStore.fulfilled, (state, action) => {
        const editedStore = action.meta.arg
        state.stores = state.stores.map((store) => {
          if (store.shopId === editedStore.id) return { ...editedStore.store, shopId: editedStore.id };
          return store
        })
        state.loading = false
        state.showModal = false
      })
      .addCase(removeStore.pending, (state) => {
        state.loading = true
      })
      .addCase(removeStore.fulfilled, (state, action) => {
        const deletedStore = action.meta.arg

        state.stores = state.stores.filter((s) => s.shopId !== deletedStore);
        state.loading = false
        state.showDeleteModal = false
      })
      .addCase(importStores.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importStores.fulfilled, (state) => {
        state.loading = false;
        // Optional: If your backend returns the newly created shops, you could merge them here:
        // state.stores = [...state.stores, ...action.payload];
      })
      .addCase(importStores.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  addPhotoToStore
} = storeSlice.actions;

// Selectors
/**
 * Selector: Get stores user has permission to access
 * Memoized to prevent unnecessary re-renders
 */
export const selectAccessibleStores = createSelector(
  [(state) => state.store.stores, (state) => state.auth.user],
  (allStores, currentUser) => {
    if (!currentUser) return [];

    // ADMIN can see all stores
    if (isAdmin(currentUser)) {
      return allStores;
    }

    // Non-admin users only see their assigned stores
    return allStores.filter(store =>
      currentUser.shopIds && currentUser.shopIds.includes(store.shopId)
    );
  }
);

/**
 * Selector: Combined shop filtering + permission filtering for stores
 * Memoized to prevent unnecessary re-renders
 */
export const selectFilteredAccessibleStores = createSelector(
  [
    (state) => state.store.stores,
    (state) => state.auth.user,
    (state, shopId) => shopId
  ],
  (allStores, currentUser, shopId) => {
    if (!currentUser) return [];

    // Get accessible stores first
    let accessibleStores;
    if (isAdmin(currentUser)) {
      accessibleStores = allStores;
    } else {
      accessibleStores = allStores.filter(store =>
        currentUser.shopIds && currentUser.shopIds.includes(store.shopId)
      );
    }

    // If "all" or no shopId, return all accessible stores
    if (!shopId || shopId === 'all') {
      return accessibleStores;
    }

    // Filter to specific shop (if user has access)
    return accessibleStores.filter(store => store.shopId === shopId);
  }
);

/**
 * Helper function to check if a shop has POS capability
 * POS types: 0 (PointOfSale), 4 (POS_LAB), 5 (POS_WARE), 7 (POS_LAB_WARE), 8 (ALL_IN_ONE)
 */
const hasPosCapability = (shopTypeCode) => {
  return [0, 4, 5, 7, 8].includes(shopTypeCode);
};

/**
 * Helper function to check if a shop has Lab capability
 * Lab types: 1 (Lab), 4 (POS_LAB), 6 (LAB_WARE), 7 (POS_LAB_WARE), 8 (ALL_IN_ONE)
 */
const hasLabCapability = (shopTypeCode) => {
  return [1, 4, 6, 7, 8].includes(shopTypeCode);
};

/**
 * Selector: Get only shops with POS capability (accessible to user)
 * Memoized to prevent unnecessary re-renders
 */
export const selectAccessiblePosShops = createSelector(
  [selectAccessibleStores],
  (accessibleStores) => {
    return accessibleStores.filter(store => hasPosCapability(store.shopTypeId));
  }
);

/**
 * Selector: Get only shops with Lab capability (accessible to user)
 * Memoized to prevent unnecessary re-renders
 */
export const selectAccessibleLabs = createSelector(
  [selectAccessibleStores],
  (accessibleStores) => {
    return accessibleStores.filter(store => hasLabCapability(store.shopTypeId));
  }
);

/**
 * Selector: Get ALL labs (no permission filtering, only shopTypeId)
 * Used for product creation where all labs should be visible
 * Memoized to prevent unnecessary re-renders
 */
export const selectAllLabs = createSelector(
  [(state) => state.store.stores],
  (allStores) => {
    return allStores.filter(store => hasLabCapability(store.shopTypeId));
  }
);

/**
 * Helper: shop de type Lab uniquement
 * OnlyLab types: 1 (Lab), 6 (LAB_WARE) – adapte si besoin - 3 ADMINISTRATION
 */
const isOnlyLab = (shopTypeCode) => {
  return [1, 6].includes(shopTypeCode);
};

const isLabOrAdministration = (shopTypeCode) => {
  return [1, 3, 6].includes(shopTypeCode);
};

export const selectAccessibleOnlyLabs = createSelector(
  [selectAccessibleStores],
  (accessibleStores) => {
    return accessibleStores.filter(store => isLabOrAdministration(store.shopTypeId));
  }
);

/**
 * Selector: ALL onlyLab (sans filtrage permissions)
 */
export const selectAllOnlyLabs = createSelector(
  [(state) => state.store.stores],
  (allStores) => {
    return allStores.filter(store => isOnlyLab(store.shopTypeId));
  }
);


export const selectAllShopsExceptAD = createSelector(
  [(state) => state.store.stores],
  (allStores) => {
    return allStores.filter(store => store.shopCode !== 'AD');
  }
);

export default storeSlice.reducer;